import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasFeatureAccess, getTierDisplayName, getRequiredTier } from '../config/featureAccess';
import { Trophy, Lock, Zap, TrendingUp, Star } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';

const LeaderboardWidget = ({ userStats, leaderboard = [] }) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { tier } = useSubscription();
  const userTier = tier || profile?.subscription_tier || 'free';
  const hasAccess = hasFeatureAccess('leaderboards', userTier);

  // Premium Lock Screen for FREE users
  if (!hasAccess) {
    const wittyMessages = [
      {
        headline: "Top Performers Are Crushing It 🔥",
        subtext: "But we can't let you see their secrets... yet.",
        cta: "Join the competition"
      },
      {
        headline: "Glory Awaits on the Leaderboard 🏆",
        subtext: "Spoiler: Someone named Sarah is dominating. Will you beat her?",
        cta: "Unlock leaderboards"
      },
      {
        headline: "You Can't Win If You're Not Playing 🎯",
        subtext: "Free users practice alone. Champions compete.",
        cta: "Start competing"
      },
      {
        headline: "The Top 10 Are Watching 👀",
        subtext: "They're wondering if you have what it takes to join them.",
        cta: "Prove yourself"
      },
      {
        headline: "Ranked #??? (Because You're Not Ranked) 😅",
        subtext: "Upgrade to see where you'd actually rank against the best.",
        cta: "Get on the board"
      }
    ];

    // Pick message based on day of week for variety
    const dayIndex = new Date().getDay();
    const message = wittyMessages[dayIndex % wittyMessages.length];

    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 shadow-2xl">
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.1))]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        <div className="relative z-10 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 p-2.5 shadow-lg">
                <Trophy className="w-full h-full text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Leaderboard</h3>
            </div>
            <div className="px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/50 flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-semibold text-amber-400 uppercase">{getTierDisplayName(getRequiredTier('leaderboards'))}</span>
            </div>
          </div>

          {/* Blurred Fake Leaderboard Background */}
          <div className="mb-6 relative">
            <div className="absolute inset-0 backdrop-blur-xl bg-white/5 z-10 rounded-xl" />
            <div className="space-y-3 opacity-30">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white/10 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-white/30 rounded mb-1" />
                    <div className="h-3 w-20 bg-white/20 rounded" />
                  </div>
                  <div className="h-4 w-12 bg-white/30 rounded" />
                </div>
              ))}
            </div>
          </div>

          {/* Witty Message */}
          <div className="text-center mb-6">
            <h4 className="text-2xl font-bold text-white mb-2">
              {message.headline}
            </h4>
            <p className="text-gray-300 text-base mb-4">
              {message.subtext}
            </p>
          </div>

          {/* Feature Pills */}
          <div className="grid grid-cols-3 gap-2 mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-white/20">
              <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
              <p className="text-xs text-gray-300">Global Ranks</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-white/20">
              <Zap className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <p className="text-xs text-gray-300">XP Tracking</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-white/20">
              <Star className="w-5 h-5 text-purple-400 mx-auto mb-1" />
              <p className="text-xs text-gray-300">Compete</p>
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={() => navigate('/app/subscription')}
            className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Trophy className="w-5 h-5" />
            {message.cta}
          </button>

          {/* Fine print */}
          <p className="text-center text-xs text-gray-400 mt-4">
            Unlock with {getTierDisplayName(getRequiredTier('leaderboards'))} or higher
          </p>
        </div>
      </div>
    );
  }

  // Original leaderboard for subscribed users

  if (!userStats && leaderboard.length === 0) {
    return null;
  }

  const getRankBadge = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankChange = (change) => {
    if (!change || change === 0) return null;
    if (change > 0) return <span className="text-green-600 text-xs ml-1">↑{change}</span>;
    return <span className="text-red-600 text-xs ml-1">↓{Math.abs(change)}</span>;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          🏆 Leaderboard
        </h3>
        <button
          onClick={() => navigate('/app/leaderboards')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          View All →
        </button>
      </div>

      {/* User's Current Rank */}
      {userStats && (
        <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Your Global Rank</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900">
                  {userStats.global_rank ? getRankBadge(userStats.global_rank) : '-'}
                </span>
                {userStats.rank_change && getRankChange(userStats.rank_change)}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-600">Total XP</p>
              <p className="text-lg font-bold text-blue-600">
                {userStats.total_xp?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Top 5 Leaderboard */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Top Performers
        </p>
        {leaderboard.slice(0, 5).map((entry, index) => (
          <div
            key={entry.id}
            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold w-8">
                {getRankBadge(index + 1)}
              </span>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                  {entry.user?.full_name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {entry.user?.full_name || 'Anonymous'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {entry.total_xp?.toLocaleString() || 0} XP
                  </p>
                </div>
              </div>
            </div>
            {entry.longest_streak > 0 && (
              <div className="text-xs text-gray-600">
                🔥 {entry.longest_streak}d
              </div>
            )}
          </div>
        ))}
      </div>

      {leaderboard.length === 0 && (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">🏆</div>
          <p className="text-sm text-gray-600">
            Complete your first drill to join the leaderboard!
          </p>
        </div>
      )}
    </div>
  );
};

export default LeaderboardWidget;
